import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Phone, MessageSquare, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { LoadingState, ErrorMessage } from "@/components/ui/States";
import { track, EVENTS } from "@/lib/analytics";
import {
    WorkflowPanel,
    PaymentsPanel,
    PaymentLinkCard,
    GHLPanel,
    MakePanel,
    PortalPanel,
} from "@/pages/admin/LeadDetailPhase8Panels";
import DraftReplyPanel from "@/components/admin/DraftReplyPanel";
import ReviewInviteCard from "@/components/admin/ReviewInviteCard";

const STATUS_OPTIONS = ["new", "contacted", "quoted", "scheduled", "closed-won", "closed-lost"];
const URGENCY_OPTIONS = [
    "P1-storm",
    "P1-safety-flag",
    "P2-hoa-deadline",
    "P3-active-pest",
    "P4-rescreen",
    "P5-door",
    "P6-routine",
    "P7-property-manager",
    "P8-need-info",
];

const TABS = [
    { id: "overview", label: "Overview" },
    { id: "notes", label: "Notes" },
    { id: "payments", label: "Workflow & Payments" },
    { id: "integrations", label: "Integrations" },
    { id: "ai-draft", label: "AI Draft" },
];

export default function LeadDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState("overview");
    const [noteBody, setNoteBody] = useState("");
    const [savingNote, setSavingNote] = useState(false);

    const load = () => {
        setLoading(true);
        api.get(`/admin/leads/${id}`)
            .then((r) => setData(r.data))
            .catch((err) => setError(err?.response?.data?.detail || "Couldn't load lead"))
            .finally(() => setLoading(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [id]);

    if (loading) return <LoadingState />;
    if (error) return <ErrorMessage message={error} />;
    if (!data) return null;

    const { lead, photos, notes } = data;

    const updateStatus = async (status) => {
        await api.patch(`/admin/leads/${lead.id}/status`, { status });
        track(EVENTS.ADMIN_LEAD_REVIEWED, { lead_id: lead.id, status });
        load();
    };

    const updateUrgency = async (urgency_tag) => {
        const reason = window.prompt("Reason for override (required):", "") || "";
        if (!reason.trim()) return;
        await api.patch(`/admin/leads/${lead.id}/urgency`, { urgency_tag, reason });
        load();
    };

    const toggleHumanReview = async () => {
        await api.patch(`/admin/leads/${lead.id}/human-review`, {
            requires_human_review: !lead.requires_human_review,
        });
        if (lead.requires_human_review) {
            track(EVENTS.HUMAN_REVIEW_COMPLETED, { lead_id: lead.id });
        }
        load();
    };

    const addNote = async (e) => {
        e.preventDefault();
        if (!noteBody.trim()) return;
        setSavingNote(true);
        try {
            await api.post(`/admin/leads/${lead.id}/notes`, { body: noteBody });
            setNoteBody("");
            load();
        } finally {
            setSavingNote(false);
        }
    };

    return (
        <div data-testid="admin-lead-detail">
            <Link to="/admin/leads" className="a1-btn-ghost mb-4"><ArrowLeft size={16} /> Back to leads</Link>

            <div className="a1-card mb-4">
                {lead.is_test_seed ? (
                    <div
                        className="mb-3 px-3 py-2 rounded-md flex items-center gap-2 text-sm font-bold"
                        style={{
                            background: "rgba(231, 119, 78, 0.1)",
                            border: "1px solid var(--color-storm)",
                            color: "var(--color-storm)",
                        }}
                        data-testid="lead-test-seed-banner"
                    >
                        <span className="a1-badge" style={{ background: "var(--color-storm)", color: "white" }}>TEST · DRY-RUN</span>
                        Tester Week sample — {lead.test_seed_label || "not a real customer"}
                    </div>
                ) : null}
                <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                    <div>
                        <p className="text-sm text-[var(--color-text-muted)]">{lead.urgency_tag} · {lead.site_mode}</p>
                        <h1 className="text-2xl">{lead.name || "(no name)"} — {lead.city}</h1>
                        <p className="text-sm">
                            <a href={`tel:${lead.phone}`}>{lead.phone}</a>
                            {lead.email ? <> · <a href={`mailto:${lead.email}`}>{lead.email}</a></> : null}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <a href={`tel:${lead.phone}`} className="a1-btn-secondary"><Phone size={16} /> Call</a>
                        <a href={`sms:${lead.phone}`} className="a1-btn-secondary"><MessageSquare size={16} /> Text</a>
                        <button onClick={() => updateStatus("contacted")} className="a1-btn-primary" data-testid="lead-mark-contacted">
                            <CheckCircle2 size={16} /> Mark Contacted
                        </button>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-2 text-sm mt-2">
                    <div><strong>Service:</strong> {lead.service_needed || "—"}</div>
                    <div><strong>Project type:</strong> {lead.project_type || "—"}</div>
                    <div><strong>Urgency input:</strong> {lead.urgency_input || "—"}</div>
                    <div><strong>Source:</strong> {lead.lead_source}</div>
                    <div><strong>Preferred contact:</strong> {lead.preferred_contact || "—"}</div>
                    <div><strong>Best time:</strong> {lead.best_time || "—"}</div>
                    {lead.panel_count ? <div data-testid="lead-panel-count"><strong>Panel count:</strong> {lead.panel_count}</div> : null}
                    {lead.access_difficulty ? <div data-testid="lead-access-difficulty"><strong>Access difficulty:</strong> {lead.access_difficulty}</div> : null}
                    {lead.job_detail ? <div data-testid="lead-job-detail"><strong>Job detail:</strong> {lead.job_detail}</div> : null}
                </div>
                {lead.notes ? (
                    <div className="mt-3">
                        <strong>Customer notes:</strong>
                        <p className="text-[var(--color-text-muted)] whitespace-pre-line">{lead.notes}</p>
                    </div>
                ) : null}
            </div>

            {lead.status === "closed-won" ? <ReviewInviteCard lead={lead} onChange={load} /> : null}

            {/* Tabs — replaces the old 8-panel vertical scroll */}
            <div className="admin-tab-bar" role="tablist" aria-label="Lead sections" data-testid="lead-detail-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.id}
                        className={`admin-tab${tab === t.id ? " is-active" : ""}`}
                        onClick={() => setTab(t.id)}
                        data-testid={`lead-tab-${t.id}`}
                    >
                        {t.label}
                        {t.id === "notes" && notes.length ? <span className="admin-tab-count">{notes.length}</span> : null}
                        {t.id === "overview" && photos.length ? <span className="admin-tab-count">{photos.length} 📷</span> : null}
                    </button>
                ))}
            </div>

            {tab === "overview" ? (
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="a1-card">
                        <h3 className="text-lg mb-2">Status & Urgency</h3>
                        <label className="a1-label">Status</label>
                        <select
                            className="a1-select mb-3"
                            value={lead.status}
                            onChange={(e) => updateStatus(e.target.value)}
                            data-testid="lead-status-select"
                        >
                            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <label className="a1-label">Urgency (override)</label>
                        <select
                            className="a1-select mb-3"
                            value={lead.urgency_tag}
                            onChange={(e) => updateUrgency(e.target.value)}
                            data-testid="lead-urgency-select"
                        >
                            {URGENCY_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                        <button
                            onClick={toggleHumanReview}
                            className={lead.requires_human_review ? "a1-btn-primary w-full" : "a1-btn-secondary w-full"}
                            data-testid="lead-human-review-toggle"
                        >
                            {lead.requires_human_review ? "Mark Technician Review Complete" : "Reopen Technician Review"}
                        </button>
                    </div>

                    <div className="a1-card">
                        <h3 className="text-lg mb-2">Photos ({photos.length})</h3>
                        {photos.length === 0 ? (
                            <p className="text-sm text-[var(--color-text-muted)]">No photos uploaded.</p>
                        ) : (
                            <ul className="text-sm space-y-1">
                                {photos.map((p) => (
                                    <li key={p.id || p.url}>
                                        📷 {p.original_filename || p.url || "(metadata only)"} — {p.is_safe_to_publish ? "✅ approved" : "⏳ pending review"}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <p className="text-xs text-[var(--color-text-muted)] italic mt-3">
                            Photos default to NOT safe to publish. Approve before any gallery use.
                        </p>
                    </div>
                </div>
            ) : null}

            {tab === "notes" ? (
                <div className="a1-card">
                    <h3 className="text-lg mb-2">Internal Notes</h3>
                    <form onSubmit={addNote} className="flex gap-2 mb-3">
                        <input className="a1-input flex-1" value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="Add a note…" data-testid="lead-note-input" />
                        <button type="submit" className="a1-btn-primary" disabled={savingNote || !noteBody.trim()} data-testid="lead-note-submit">
                            {savingNote ? "Saving…" : "Add Note"}
                        </button>
                    </form>
                    {notes.length === 0 ? (
                        <p className="text-sm text-[var(--color-text-muted)]">No notes yet.</p>
                    ) : (
                        <ul className="text-sm space-y-2">
                            {notes.map((n) => (
                                <li key={n.id} className="border-b border-[var(--color-line)] pb-2">
                                    <div className="text-xs text-[var(--color-text-muted)]">{n.author_email} · {n.created_at}</div>
                                    <div>{n.body}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            ) : null}

            {tab === "payments" ? (
                <div className="space-y-4">
                    <WorkflowPanel lead={lead} onChange={load} />
                    <PaymentsPanel lead={lead} onChange={load} />
                    <PaymentLinkCard lead={lead} onChange={load} />
                </div>
            ) : null}

            {tab === "integrations" ? (
                <div className="space-y-4">
                    <GHLPanel lead={lead} onChange={load} />
                    <MakePanel lead={lead} />
                    <PortalPanel lead={lead} onChange={load} />
                </div>
            ) : null}

            {tab === "ai-draft" ? <DraftReplyPanel lead={lead} /> : null}
        </div>
    );
}
